import { logUnexpectedError } from '@/helpers'
import { getWindow } from '@/helpers/globals'
import { useSegment } from '@/third-party/segment'

type UseAuthTracking = {
    trackSso(
        provider: SsoProvider,
        event: SsoEvent,
        procedure: AuthProcedure
    ): void
    trackEmail(event: EmailEvent, procedure: AuthProcedure): void
    trackClick(provider: SsoProvider | 'email', procedure: AuthProcedure): void
    trackAuthNavigation(navigateTo: AuthProcedure): void
    trackMagicLinkClick(): void
    trackMarketingOptInToggle(value: boolean): void
    trackForgotPasswordClick(): void
}
type ResultEvent = 'success' | 'failure'
export type SsoProvider = 'facebook' | 'google'
type SsoEvent = 'click' | ResultEvent
type AuthProcedure = 'login' | 'registration'
type EmailEvent = 'click' | ResultEvent

const getSsoEventName = (provider: SsoProvider, event: SsoEvent) =>
    `sso_${event}_${provider}`

const getEmailEventName = (event: EmailEvent) => {
    const eventNameRoot = event === 'click' ? 'continue_with' : event

    return `email_${eventNameRoot}`
}

export function useAuthTracking(): UseAuthTracking {
    const { track: segmentTrack } = useSegment()

    const window = getWindow()

    const getBaseParams = () => {
        if (!window) {
            logUnexpectedError('Auth tracking: Unable to find window', {})

            return {}
        }

        return {
            param4: window.location.href,
            url: window.location.href,
        }
    }

    const track = (event_name: string, params: Record<string, string>) => {
        segmentTrack(`custom_${event_name}`, {
            ...params,
            ...getBaseParams(),
            event_name,
        })
    }

    const trackSso = (
        provider: SsoProvider,
        event: SsoEvent,
        procedure: AuthProcedure
    ) => {
        const eventName = getSsoEventName(provider, event)

        track(eventName, {
            param1: provider,
            param2: event,
            param3: procedure,
        })
    }

    const trackEmail = (event: EmailEvent, procedure: AuthProcedure) => {
        const eventName = getEmailEventName(event)

        track(eventName, {
            param1: 'email',
            param2: event,
            param3: procedure,
        })
    }

    const trackAuthNavigation = (navigateTo: AuthProcedure) => {
        const eventNamePrefix = 'go_to'
        const event = `${eventNamePrefix}_${navigateTo}`
        const eventName = `${event}_click`

        track(eventName, {
            param1: event,
            param2: 'click',
            param3: navigateTo === 'login' ? 'registration' : 'login',
        })
    }

    const trackForgotPasswordClick = () => {
        track('forgot_password_click', {
            param1: 'forgot_password',
            param2: 'click',
            param3: 'login',
        })
    }

    const trackMagicLinkClick = () => {
        track('magic_link_click', {
            param1: 'magic_link',
            param2: 'click',
            param3: 'login',
        })
    }

    const trackMarketingOptInToggle = (value: boolean) => {
        track('marketing_optin_toggle', {
            param1: 'marketing_optin',
            param2: 'toggle',
            param3: value.toString(),
        })
    }

    const trackClick = (
        provider: SsoProvider | 'email',
        procedure: AuthProcedure
    ) => {
        if (provider === 'email') {
            trackEmail('click', procedure)
        } else {
            trackSso(provider, 'click', procedure)
        }
    }

    return {
        trackSso,
        trackEmail,
        trackClick,
        trackAuthNavigation,
        trackForgotPasswordClick,
        trackMagicLinkClick,
        trackMarketingOptInToggle,
    }
}
